import { useState, useEffect, useCallback } from 'react';
import { adminApi, sectorsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Settings, Plus, Pencil, Trash2, Filter, ChevronDown, ChevronUp, X, Calculator, ArrowUpDown, Gauge, Tag, Info, Zap, Scale } from 'lucide-react';

const SCOPE_LABELS = { core: 'Universel', sector: 'Sectoriel', addon: 'Addon' };
const DIR_LABELS = { higher_is_better: 'Plus = mieux', lower_is_better: 'Moins = mieux' };
const FMT_LABELS = { number: 'Nombre', currency: 'Monnaie', percentage: 'Pourcentage', decimal: 'Decimal' };

const EURO_METHOD_LABELS = {
  gap_x_clients: 'Ecart x Clients',
  gap_x_ticket_x_days: 'Ecart x Ticket x Jours',
  gap_x_hours: 'Ecart x Heures',
  pct_gap_x_ca: '% Ecart x CA',
  direct_gap: 'Ecart direct',
  gap_x_ca_jour: 'Ecart x CA/jour',
  direct_value: 'Valeur directe',
};

const EMPTY_KPI = {
  kpi_id: '', name: '', description: '', unit: '', format: 'number',
  scope: 'core', sector_id: null, formula: '', inputs_required: [],
  direction: 'higher_is_better', weight: 1.0, enabled: true,
  module: '', methodology: '', explanation: '', action_tip: '',
  euro_explanation: '', euro_method: '', is_primary_lever: false, order: 100,
};

export default function KPIsPage() {
  const [kpis, setKpis] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filterScope, setFilterScope] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [editKpi, setEditKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const sectorMap = sectors.reduce((m, s) => ({ ...m, [s.id]: s.name }), {});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterScope) params.scope = filterScope;
      if (filterSector) params.sector_id = filterSector;
      const [kRes, sRes] = await Promise.all([adminApi.kpis(params), sectorsApi.list()]);
      setKpis(kRes.data);
      setSectors(sRes.data);
    } catch (e) {
      toast.error('Erreur chargement KPIs');
    }
    setLoading(false);
  }, [filterScope, filterSector]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (kpi) => {
    try {
      const params = { scope: kpi.scope };
      if (kpi.sector_id) params.sector_id = kpi.sector_id;
      await adminApi.toggleKpi(kpi.kpi_id, params);
      toast.success(`${kpi.name} ${kpi.enabled ? 'desactive' : 'active'}`);
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (kpi) => {
    if (!window.confirm(`Supprimer "${kpi.name}" ?`)) return;
    try {
      const params = { scope: kpi.scope };
      if (kpi.sector_id) params.sector_id = kpi.sector_id;
      await adminApi.deleteKpi(kpi.kpi_id, params);
      toast.success('KPI supprime');
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleSave = async (data) => {
    try {
      if (editKpi?.kpi_id && editKpi?.kpi_id === data.kpi_id) {
        const params = { scope: editKpi.scope };
        if (editKpi.sector_id) params.sector_id = editKpi.sector_id;
        await adminApi.updateKpi(data.kpi_id, data, params);
        toast.success('KPI mis a jour');
      } else {
        await adminApi.createKpi(data);
        toast.success('KPI cree');
      }
      setEditKpi(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const coreCount = kpis.filter(k => k.scope === 'core').length;
  const sectorCount = kpis.filter(k => k.scope === 'sector').length;
  const addonCount = kpis.filter(k => k.scope === 'addon').length;
  const activeCount = kpis.filter(k => k.enabled).length;

  const getExpandedKey = (kpi) => kpi.kpi_id + (kpi.sector_id || '');

  return (
    <div data-testid="kpis-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2">
            <Settings className="h-6 w-6" /> Gestion des KPIs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount} actifs sur {kpis.length} — {coreCount} universels, {sectorCount} sectoriels{addonCount > 0 ? `, ${addonCount} addons` : ''}
          </p>
        </div>
        <Button data-testid="create-kpi-btn" onClick={() => setEditKpi({...EMPTY_KPI})} className="bg-[#1B2A4A] hover:bg-[#2a3d5e]">
          <Plus className="h-4 w-4 mr-2" /> Nouveau KPI
        </Button>
      </div>

      <div className="flex gap-3 items-center flex-wrap bg-white p-4 rounded-xl border">
        <Filter className="h-4 w-4 text-gray-400" />
        <select data-testid="filter-scope" value={filterScope} onChange={e => setFilterScope(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">Tous les scopes</option>
          <option value="core">Universel</option>
          <option value="sector">Sectoriel</option>
          <option value="addon">Addon</option>
        </select>
        <select data-testid="filter-sector" value={filterSector} onChange={e => setFilterSector(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">Tous les secteurs</option>
          {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : (
        <div className="space-y-2">
          {kpis.map((kpi) => {
            const isExpanded = expanded === getExpandedKey(kpi);
            return (
              <div key={getExpandedKey(kpi)} className="bg-white rounded-xl border overflow-hidden" data-testid={`kpi-row-${kpi.kpi_id}`}>
                {/* Main row */}
                <div
                  className="flex items-center px-5 py-3.5 cursor-pointer hover:bg-gray-50/50 transition-all"
                  onClick={() => setExpanded(isExpanded ? null : getExpandedKey(kpi))}
                >
                  <div className="w-10 text-gray-400 font-mono text-xs">{kpi.order}</div>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#1B2A4A] text-sm">{kpi.name}</span>
                      {!kpi.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">Inactif</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-mono text-gray-400">{kpi.kpi_id}</span>
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${kpi.scope === 'core' ? 'bg-blue-50 text-blue-600' : kpi.scope === 'addon' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-amber-600'}`}>
                        {SCOPE_LABELS[kpi.scope]}
                      </span>
                      {kpi.scope === 'sector' && kpi.sector_id && (
                        <span className="text-[10px] text-gray-400">{sectorMap[kpi.sector_id]}</span>
                      )}
                      {kpi.module && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500">{kpi.module}</span>}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-3 mr-4">
                    <code className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded max-w-[180px] truncate">{kpi.formula}</code>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{DIR_LABELS[kpi.direction]}</span>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <Switch data-testid={`toggle-kpi-${kpi.kpi_id}`} checked={kpi.enabled} onCheckedChange={() => handleToggle(kpi)} />
                    <button data-testid={`edit-kpi-${kpi.kpi_id}`} onClick={() => setEditKpi(kpi)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`delete-kpi-${kpi.kpi_id}`} onClick={() => handleDelete(kpi)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div className="border-t bg-[#F8F9FA] px-5 py-4 space-y-4" data-testid={`kpi-detail-${kpi.kpi_id}`}>
                    {/* Row 1: Formula + Inputs + Direction */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <DetailCard icon={<Calculator size={14} />} title="Formule de calcul" color="blue">
                        <code className="text-sm font-mono text-[#1B2A4A] break-all">{kpi.formula || '—'}</code>
                      </DetailCard>
                      <DetailCard icon={<Tag size={14} />} title="Variables requises" color="amber">
                        {kpi.inputs_required?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {kpi.inputs_required.map(v => (
                              <span key={v} className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-mono">{v}</span>
                            ))}
                          </div>
                        ) : <span className="text-xs text-gray-400">Aucune</span>}
                      </DetailCard>
                      <DetailCard icon={<ArrowUpDown size={14} />} title="Direction & Poids" color="indigo">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${kpi.direction === 'higher_is_better' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                              {DIR_LABELS[kpi.direction]}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">Poids : <strong className="text-[#1B2A4A]">{kpi.weight}</strong> — Format : <strong className="text-[#1B2A4A]">{FMT_LABELS[kpi.format] || kpi.format}</strong> — Unite : <strong className="text-[#1B2A4A]">{kpi.unit || '—'}</strong></p>
                        </div>
                      </DetailCard>
                    </div>

                    {/* Row 2: Impact Euro + Methodology */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DetailCard icon={<Scale size={14} />} title="Methode impact Euro" color="emerald">
                        <div className="space-y-1">
                          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                            {kpi.euro_method ? (EURO_METHOD_LABELS[kpi.euro_method] || kpi.euro_method) : 'Non defini'}
                          </span>
                          {kpi.euro_explanation && <p className="text-xs text-gray-600 mt-1">{kpi.euro_explanation}</p>}
                          {kpi.is_primary_lever && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B8860B] mt-1">
                              <Zap size={10} /> Levier primaire
                            </span>
                          )}
                        </div>
                      </DetailCard>
                      <DetailCard icon={<Info size={14} />} title="Methodologie & Conseil" color="gray">
                        <div className="space-y-1">
                          {kpi.methodology && <p className="text-xs text-gray-600"><strong>Methodologie :</strong> {kpi.methodology}</p>}
                          {kpi.explanation && <p className="text-xs text-gray-600"><strong>Explication :</strong> {kpi.explanation}</p>}
                          {kpi.action_tip && <p className="text-xs text-gray-600"><strong>Conseil :</strong> {kpi.action_tip}</p>}
                          {kpi.description && <p className="text-xs text-gray-600"><strong>Description :</strong> {kpi.description}</p>}
                          {!kpi.methodology && !kpi.explanation && !kpi.action_tip && !kpi.description && (
                            <p className="text-xs text-gray-400 italic">Aucune documentation renseignee</p>
                          )}
                        </div>
                      </DetailCard>
                    </div>

                    {/* Row 3: Effort fields if present */}
                    {(kpi.effort_max_30j || kpi.effort_description) && (
                      <DetailCard icon={<Gauge size={14} />} title="Effort envisageable (30 jours)" color="violet">
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          {kpi.effort_max_30j && <span>Max : <strong className="text-[#1B2A4A]">{kpi.effort_max_30j} {kpi.effort_unite || ''}</strong></span>}
                          {kpi.effort_description && <span>{kpi.effort_description}</span>}
                        </div>
                      </DetailCard>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {kpis.length === 0 && (
            <div className="bg-white rounded-xl border p-8 text-center text-gray-400">Aucun KPI trouve</div>
          )}
        </div>
      )}

      {editKpi && <KpiModal kpi={editKpi} sectors={sectors} onClose={() => setEditKpi(null)} onSave={handleSave} />}
    </div>
  );
}

function DetailCard({ icon, title, color, children }) {
  const borderColor = {
    blue: 'border-blue-200', amber: 'border-amber-200', indigo: 'border-indigo-200',
    emerald: 'border-emerald-200', gray: 'border-gray-200', violet: 'border-violet-200',
  }[color] || 'border-gray-200';
  const iconColor = {
    blue: 'text-blue-600', amber: 'text-amber-600', indigo: 'text-indigo-600',
    emerald: 'text-emerald-600', gray: 'text-gray-500', violet: 'text-violet-600',
  }[color] || 'text-gray-500';

  return (
    <div className={`bg-white rounded-lg border ${borderColor} p-3`}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className={iconColor}>{icon}</span>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function KpiModal({ kpi, sectors, onClose, onSave }) {
  const [form, setForm] = useState({ ...EMPTY_KPI, ...kpi, inputs_required: kpi.inputs_required || [] });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      inputs_required: typeof form.inputs_required === 'string'
        ? form.inputs_required.split(',').map(s => s.trim()).filter(Boolean)
        : form.inputs_required,
      sector_id: form.scope === 'sector' ? form.sector_id : null,
    };
    onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-8 overflow-y-auto" onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 mb-8" data-testid="kpi-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-[#1B2A4A]">{kpi.kpi_id ? 'Modifier le KPI' : 'Nouveau KPI'}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Identifiant (slug)" value={form.kpi_id} onChange={v => set('kpi_id', v)} required testid="kpi-id-input" />
            <Field label="Nom" value={form.name} onChange={v => set('name', v)} required testid="kpi-name-input" />
          </div>
          <Field label="Description" value={form.description} onChange={v => set('description', v)} textarea testid="kpi-desc-input" />
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Scope</label>
              <select data-testid="kpi-scope-select" value={form.scope} onChange={e => set('scope', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="core">Universel</option>
                <option value="sector">Sectoriel</option>
                <option value="addon">Addon</option>
              </select>
            </div>
            {form.scope === 'sector' && (
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Secteur</label>
                <select data-testid="kpi-sector-select" value={form.sector_id || ''} onChange={e => set('sector_id', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="">Choisir un secteur</option>
                  {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <Field label="Module" value={form.module} onChange={v => set('module', v)} testid="kpi-module-input" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Unite" value={form.unit} onChange={v => set('unit', v)} testid="kpi-unit-input" />
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Format</label>
              <select data-testid="kpi-format-select" value={form.format} onChange={e => set('format', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="number">Nombre</option>
                <option value="currency">Monnaie</option>
                <option value="percentage">Pourcentage</option>
                <option value="decimal">Decimal</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Direction</label>
              <select data-testid="kpi-direction-select" value={form.direction} onChange={e => set('direction', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="higher_is_better">Plus = mieux</option>
                <option value="lower_is_better">Moins = mieux</option>
              </select>
            </div>
          </div>
          <Field label="Formule (ex: ca / clients)" value={form.formula} onChange={v => set('formula', v)} required testid="kpi-formula-input" />
          <Field label="Variables requises (separees par des virgules)" testid="kpi-inputs-input"
            value={Array.isArray(form.inputs_required) ? form.inputs_required.join(', ') : form.inputs_required}
            onChange={v => set('inputs_required', v)} />
          <p className="text-xs text-gray-400">Variables disponibles : ca, clients, jours, cap, hrs, emp, surf, mat, pers, energie, stock, impots, achats, charges_ext, ordo, ca_para, ca_vente_libre, ca_remboursable, taux_gen, tp_soumis, tp_rejete, tp_en_attente, heures_travaillees, etp, surface_vente, surface_stockage</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Ordre d'affichage" value={form.order} onChange={v => set('order', parseInt(v) || 0)} type="number" testid="kpi-order-input" />
            <Field label="Poids" value={form.weight} onChange={v => set('weight', parseFloat(v) || 1)} type="number" testid="kpi-weight-input" />
          </div>
          <Field label="Methodologie" value={form.methodology} onChange={v => set('methodology', v)} textarea testid="kpi-methodology-input" />
          <Field label="Conseil d'action" value={form.action_tip} onChange={v => set('action_tip', v)} textarea testid="kpi-action-input" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Methode impact Euro</label>
              <select data-testid="kpi-euromethod-input" value={form.euro_method || ''} onChange={e => set('euro_method', e.target.value || null)}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Aucune</option>
                {Object.entries(EURO_METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-4 pt-5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_primary_lever} onChange={e => set('is_primary_lever', e.target.checked)} className="rounded" />
                Levier primaire
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} className="rounded" />
                Actif
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" data-testid="kpi-save-btn" className="bg-[#1B2A4A] hover:bg-[#2a3d5e]">Enregistrer</Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, textarea, type = 'text', required, testid }) {
  const cls = "w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] outline-none";
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 block mb-1">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value || ''} onChange={e => onChange(e.target.value)}
          className={cls + " h-16 resize-none"} required={required} />
      ) : (
        <input data-testid={testid} type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
          className={cls} required={required} />
      )}
    </div>
  );
}
